// Login-phase telemetry to Application Insights. A login journey (loginJourneyId)
// runs until the user is actually in, so an MSAL redirect login — which spans two
// page loads — stays one journey; it is closed on success and on an explicit
// restart or account switch. After the initial login completes, success events
// become no-ops so silent refresh cycles do not pollute the login funnel; failures
// are always sent with trigger 'refresh' instead of 'initial', capped per
// phase+errorCode so a retry loop cannot flood the funnel — for refresh failures
// per browser per day, because there the repetition is across page loads.

import * as UUID from 'uuid'
import { appInsights } from './appInsights'
import { getConsoleBufferSnapshot } from './consoleBuffer'
import { isOfficeInitialized } from './outlookHelper'

export type LoginPhase = 'started' | 'redirect-callback' | 'bootstrap' | 'hosts' | 'tokens' | 'authenticated' | 'reactive-refresh'
export type LoginMode = 'office-sso' | 'msal-silent' | 'msal-redirect' | 'storage'

const EVENT_NAMES: Record<LoginPhase, string> = {
    started: 'Login.Started',
    'redirect-callback': 'Login.Failed',
    bootstrap: 'Login.BootstrapAcquired',
    hosts: 'Login.HostsAcquired',
    tokens: 'Login.TokensAcquired',
    authenticated: 'Login.Authenticated',
    'reactive-refresh': 'Login.Failed',
}

const JOURNEY_KEY = 'global-loginJourney'

interface LoginJourney {
    id: string
    /** Epoch ms, so the journey can outlive the page load that started it. */
    startedAt: number
    /** How many page loads this journey has spanned so far; 1 on a plain login. */
    loads: number
}

function newJourney(): LoginJourney {
    return { id: UUID.v4(), startedAt: Date.now(), loads: 1 }
}

/**
 * Resume the journey persisted by a previous page load, or start a new one. Only
 * the standalone MSAL redirect spans page loads; in the Outlook add-in the task
 * pane never navigates away, so a fresh journey per page load is already correct
 * there — which is why falling back to `newJourney()` when sessionStorage is
 * unavailable (privacy mode, a webview that partitions it) loses nothing.
 */
function resumeJourney(): LoginJourney {
    try {
        const raw = window.sessionStorage.getItem(JOURNEY_KEY)
        const stored = raw ? (JSON.parse(raw) as Partial<LoginJourney>) : undefined
        const journey: LoginJourney =
            stored?.id && stored.startedAt ? { id: stored.id, startedAt: stored.startedAt, loads: (stored.loads ?? 0) + 1 } : newJourney()
        window.sessionStorage.setItem(JOURNEY_KEY, JSON.stringify(journey))
        return journey
    } catch {
        return newJourney()
    }
}

const journey = resumeJourney()
export const loginJourneyId = journey.id
const startedAt = performance.now()
let initialLoginComplete = false
let tokensFetchedThisSession = false

/**
 * Emitted failures per `phase|errorCode`. Kept in memory rather than in the
 * journey: the retry loops that produce the noise (Office SSO 13013, stale
 * bootstrap tokens) all run inside one page load, and carrying the count across
 * a redirect would wrongly suppress the first attempt after that redirect.
 */
const failuresPerKey = new Map<string, number>()

/**
 * Failures per key that still get their own event. Beyond this the emission thins
 * out on a log scale (10, 30, 100, …) so a retry storm costs a handful of events
 * instead of hundreds, while `failureCount` on the last one still shows the true
 * volume. Deliberate backoff failures stay visible: a lockout reads as 1, 2, 3,
 * 10, 30 … rather than disappearing.
 */
const FAILURES_KEPT_PER_KEY = 3

function shouldEmitFailure(failureCount: number): boolean {
    if (failureCount <= FAILURES_KEPT_PER_KEY) {
        return true
    }
    const magnitude = Math.pow(10, Math.floor(Math.log10(failureCount)))
    return failureCount === magnitude || failureCount === 3 * magnitude
}

/**
 * Same counting, but per browser per day instead of per page load — see `countRefreshFailure`.
 * Deliberately not cleared by `clearLocalStorageGlobals`: the noise belongs to the browser, not to
 * the account, and clearing it on an account switch would let the storm start over.
 */
const REFRESH_FAILURES_KEY = 'global-loginFailures'

interface RefreshFailureCounts {
    /** ISO date (yyyy-mm-dd) the counts belong to; a new day starts from zero. */
    day: string
    counts: Record<string, number>
}

/**
 * Refresh failures are counted per browser per day rather than per page load. The volume they
 * produce is not a retry storm inside one page load — the in-memory thinning already caps that —
 * but the same handful of failures repeated across hundreds of page loads, because the Outlook task
 * pane reloads per mail. In the August 2026 measurement one Mac browser produced 891 failure events
 * across 441 sessions, 36% of all refresh noise, none of it new information after the first few.
 *
 * Only the failures that no longer describe a login are counted this way; `initial` failures are the
 * funnel itself and always go out. Returns the running count for the day, which rides along as
 * `failureCount` so the true volume stays visible on the events that do get sent.
 */
function countRefreshFailure(key: string): number {
    const today = new Date().toISOString().substring(0, 10)
    try {
        const raw = window.localStorage.getItem(REFRESH_FAILURES_KEY)
        const stored = raw ? (JSON.parse(raw) as Partial<RefreshFailureCounts>) : undefined
        const counts = stored?.day === today ? (stored.counts ?? {}) : {}
        const count = (counts[key] ?? 0) + 1
        counts[key] = count
        window.localStorage.setItem(REFRESH_FAILURES_KEY, JSON.stringify({ day: today, counts } satisfies RefreshFailureCounts))
        return count
    } catch {
        // No localStorage (privacy mode, a webview that partitions it) or unreadable contents. Fall
        // back to the in-memory count, which is what this used to be everywhere.
        return countPageLoadFailure(key)
    }
}

function countPageLoadFailure(key: string): number {
    const count = (failuresPerKey.get(key) ?? 0) + 1
    failuresPerKey.set(key, count)
    return count
}

function getRuntime(): 'standalone' | 'outlook-windows' | 'outlook-mac' | 'outlook-web' {
    if (!isOfficeInitialized()) {
        return 'standalone'
    }
    if (Office.PlatformType.PC === Office.context.platform) {
        return 'outlook-windows'
    }
    if (Office.PlatformType.Mac === Office.context.platform) {
        return 'outlook-mac'
    }
    return 'outlook-web'
}

function commonProperties(): Record<string, unknown> {
    return {
        journeyId: loginJourneyId,
        trigger: initialLoginComplete ? 'refresh' : 'initial',
        runtime: getRuntime(),
        elapsedMs: Math.round(performance.now() - startedAt),
        // Wall-clock time since the journey began, so a login that needed a redirect
        // is measured end to end instead of restarting the clock on the second load.
        journeyElapsedMs: Date.now() - journey.startedAt,
        pageLoad: journey.loads,
    }
}

function extractErrorDetails(error: unknown): Record<string, unknown> {
    const e = error as {
        message?: string
        code?: number | string
        errorCode?: string
        response?: { status?: number }
    }
    return {
        errorMessage: String(e?.message ?? e ?? '').substring(0, 1000),
        errorCode: e?.errorCode ?? e?.code,
        httpStatus: e?.response?.status,
    }
}

export function trackLoginPhase(phase: LoginPhase, properties?: Record<string, unknown>): void {
    if (initialLoginComplete) {
        return
    }
    appInsights.trackEvent({
        name: EVENT_NAMES[phase],
        properties: {
            ...commonProperties(),
            ...(phase === 'authenticated' ? { viaStorage: !tokensFetchedThisSession } : {}),
            ...properties,
        },
    })
}

export function trackLoginFailure(phase: LoginPhase, error: unknown, properties?: Record<string, unknown>): void {
    const details = extractErrorDetails(error)
    const key = `${phase}|${details.errorCode ?? details.httpStatus ?? 'unknown'}`
    // Deliberately not called `attempt`: the bootstrap backoff already passes its own
    // `attempt` through `properties`, and that one means something else.
    // `initialLoginComplete` is exactly what makes `trigger` 'refresh', so the two stay in step.
    const failureCount = initialLoginComplete ? countRefreshFailure(key) : countPageLoadFailure(key)

    if (!shouldEmitFailure(failureCount)) {
        return
    }

    appInsights.trackEvent({
        name: 'Login.Failed',
        properties: {
            ...commonProperties(),
            phase,
            ...details,
            failureCount,
            ...properties,
            // Only the first failure of its kind carries the console buffer; the
            // repeats add kilobytes without adding information.
            ...(failureCount === 1 ? { consoleLog: getConsoleBufferSnapshot() } : {}),
        },
    })
}

export function markInitialLoginComplete(): void {
    initialLoginComplete = true
    // The user is in, so the journey is done. The in-memory id stays put for the
    // rest of this page load — later 'refresh' failures still belong to it — but
    // the next page load starts a fresh journey.
    clearLoginJourney()
}

export function markTokensFetched(): void {
    tokensFetchedThisSession = true
}

/** Close the journey on an explicit restart or account switch, before the reload. */
export function clearLoginJourney(): void {
    try {
        window.sessionStorage.removeItem(JOURNEY_KEY)
    } catch {
        // Nothing was persisted, so there is nothing to clear.
    }
}

/** Push pending telemetry out before a redirect navigates away. */
export function flushLoginTelemetry(): void {
    appInsights.flush()
}
